import {
  Controller,
  SubmitHandler,
  useForm,
  useFormState,
} from "react-hook-form";

import { FormInput, SelectForm } from "../../../../components/Input";
import {
  Field,
  FieldHelper,
  FieldLabel,
  FormCard,
  FormPanel,
  LabelRow,
  discardCopyOf,
} from "../../../../components/FormPanel";
import { AccountMultiSelect } from "../../../../components/AccountMultiSelect";
import {
  buildDraftDetails,
  ConfirmDetail,
  formatOptionalCurrency,
} from "../../../../components/PopUp/confirmShared";
import { ReturnSlider } from "../../../../components/ReturnSlider";
import {
  DateFormat,
  formatDate,
  predictedDateFromMonths,
  projectInvestmentGoal,
} from "../../../../utils";
import {
  ContributionCadence,
  CreateInvestmentGoalPayload,
} from "../../../../services";
import { valuationMessageFor } from "../../../../services/valuation";
import { MobileInvestmentGoalForm } from "./MobileInvestmentGoalForm";
import { CADENCE_OPTIONS, DEFAULT_INVESTMENT_GOAL, FORM_ID } from "./constants";
import { InvestmentGoalFormValues, InvestmentGoalPanelProps } from "./types";
import { PredictionDate, PredictionRow } from "./styles";

export const InvestmentGoalPanel = ({
  goal,
  accounts,
  isOpen,
  onClose,
  onSubmit,
}: InvestmentGoalPanelProps) => {
  const isEdit = Boolean(goal);

  const defaultValues: InvestmentGoalFormValues = goal
    ? {
        name: goal.name,
        targetAmount: goal.targetAmount,
        assumedAnnualReturn: goal.assumedAnnualReturn,
        contributionRate: goal.contributionRate,
        contributionCadence:
          CADENCE_OPTIONS.find(
            (option) => option.value === goal.contributionCadence,
          ) ?? CADENCE_OPTIONS[0],
        accountIds: goal.accountIds,
      }
    : DEFAULT_INVESTMENT_GOAL;

  const form = useForm<InvestmentGoalFormValues>({
    defaultValues,
    mode: "onBlur",
    reValidateMode: "onChange",
  });
  const { control, getValues, watch } = form;

  const { isSubmitting } = useFormState({ control });

  const watched = watch();
  const selectedAccounts = accounts.filter((account) =>
    watched.accountIds?.includes(account.id),
  );
  // The first selected account with no balance, kept rather than reduced to a boolean: it carries
  // the state that says whether the projection is waiting on a quote or blocked outright.
  const unvaluedAccount = selectedAccounts.find(
    (account) => typeof account.balance !== "number",
  );
  const hasCompleteCurrentValue = unvaluedAccount === undefined;
  const currentValue = selectedAccounts.reduce(
    (sum, account) => sum + (account.balance ?? 0),
    0,
  );
  const periodsPerYear =
    watched.contributionCadence?.value === "YEARLY" ? 1 : 12;
  const projection = hasCompleteCurrentValue
    ? projectInvestmentGoal({
        currentValue,
        target: Number(watched.targetAmount) || 0,
        annualReturn: Number(watched.assumedAnnualReturn) || 0,
        contribution: Number(watched.contributionRate) || 0,
        periodsPerYear,
      })
    : null;
  const predictedDate = projection
    ? predictedDateFromMonths(projection.monthsToTarget)
    : null;
  let predictionLabel = valuationMessageFor(unvaluedAccount?.valuation);
  if (hasCompleteCurrentValue) {
    predictionLabel = predictedDate
      ? formatDate(predictedDate, DateFormat.DMY_SPACE)
      : "Not reachable at this return";
  }

  const submit: SubmitHandler<InvestmentGoalFormValues> = (data) => {
    const payload: CreateInvestmentGoalPayload = {
      name: data.name,
      targetAmount: Number(data.targetAmount),
      assumedAnnualReturn: Number(data.assumedAnnualReturn),
      contributionRate: Number(data.contributionRate),
      contributionCadence: data.contributionCadence
        .value as ContributionCadence,
      accountIds: data.accountIds,
    };
    return onSubmit(payload, goal?.id);
  };

  const eyebrow = isEdit ? "EDIT INVESTMENT GOAL" : "NEW INVESTMENT GOAL";
  const title = isEdit ? "Edit investment goal" : "Create investment goal";
  const actionLabel = isEdit ? "Save changes" : "Create goal";
  const mobileTitle = isEdit ? "Edit goal" : "New goal";
  const mobileActionLabel = isEdit ? "Save" : "Create";

  const buildDiscardDetails = (): ConfirmDetail => {
    const values = getValues();
    const hasTarget = Number(values.targetAmount) > 0;
    const hasContribution = Number(values.contributionRate) > 0;
    const hasAccounts = values.accountIds.length > 0;
    const targetAmount = hasTarget
      ? formatOptionalCurrency(values.targetAmount)
      : undefined;

    if (isEdit) {
      return {
        pill: { label: "INVESTMENT GOAL", variant: "DEFAULT" },
        title: values.name || goal?.name || "Investment goal",
        subtitle: values.contributionCadence?.label,
        amount: targetAmount,
        amountVariant: "neutral",
      };
    }

    return buildDraftDetails({
      noun: "investment goal",
      fieldValues: [
        values.name,
        hasTarget ? values.targetAmount : null,
        hasContribution ? values.contributionRate : null,
        hasAccounts ? values.accountIds : null,
      ],
      amount: targetAmount,
      primaryLabel: values.name,
    });
  };

  const discard = {
    ...discardCopyOf(isEdit, "investment goal"),
    details: buildDiscardDetails,
  };

  return (
    <FormPanel
      open={isOpen}
      onClose={onClose}
      ariaLabel="Investment goal form"
      eyebrow={eyebrow}
      title={title}
      form={form}
      resetValues={defaultValues}
      formId={FORM_ID}
      onSubmit={submit}
      submitLabel={actionLabel}
      discard={discard}
      mobile={{
        title: mobileTitle,
        submitLabel: mobileActionLabel,
        fields: (
          <MobileInvestmentGoalForm
            control={control}
            accounts={accounts}
            predictionLabel={predictionLabel}
            isSubmitting={isSubmitting}
          />
        ),
      }}
    >
      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="investment-goal-name">Name *</FieldLabel>
          </LabelRow>
          <FormInput
            id="investment-goal-name"
            required
            name="name"
            control={control}
            handleClear={false}
            placeholder="e.g. Retirement"
            autoFocus={true}
            rules={{
              required: "Name is required!",
              minLength: {
                value: 3,
                message: "Name should have more than 3 letters!",
              },
            }}
          />
        </Field>

        <Field>
          <LabelRow>
            <FieldLabel htmlFor="investment-goal-target-amount">
              Target amount (€) *
            </FieldLabel>
          </LabelRow>
          <FormInput
            id="investment-goal-target-amount"
            required
            name="targetAmount"
            control={control}
            type="number"
            handleClear={false}
            placeholder="e.g. 100000"
            rules={{
              required: "Target amount is required!",
              min: { value: 1, message: "Target must be greater than 0!" },
            }}
          />
        </Field>
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel>Assumed annual return</FieldLabel>
          </LabelRow>
          <Controller
            name="assumedAnnualReturn"
            control={control}
            render={({ field: { value, onChange } }) => (
              <ReturnSlider value={value} onChange={onChange} />
            )}
          />
          <PredictionRow>
            <span>Estimated achievement</span>
            <PredictionDate>{predictionLabel}</PredictionDate>
          </PredictionRow>
          <FieldHelper>Projections are estimates, not guarantees.</FieldHelper>
        </Field>

        <Field>
          <LabelRow>
            <FieldLabel htmlFor="investment-goal-contribution">
              Contribution (€ / period) *
            </FieldLabel>
          </LabelRow>
          <FormInput
            id="investment-goal-contribution"
            required
            name="contributionRate"
            control={control}
            type="number"
            handleClear={false}
            placeholder="e.g. 500"
            rules={{
              required: "Contribution is required!",
              min: { value: 0, message: "Contribution cannot be negative!" },
            }}
          />
        </Field>

        <Field>
          <LabelRow>
            <FieldLabel htmlFor="investment-goal-cadence">
              Contribution cadence *
            </FieldLabel>
          </LabelRow>
          <SelectForm
            id="investment-goal-cadence"
            name="contributionCadence"
            control={control}
            defaultValue={defaultValues.contributionCadence}
            options={CADENCE_OPTIONS}
            rules={{ required: "Cadence is required!" }}
          />
        </Field>
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel>Linked investment accounts *</FieldLabel>
          </LabelRow>
          <Controller
            name="accountIds"
            control={control}
            rules={{
              validate: (value: number[]) =>
                value.length > 0 || "Link at least one account!",
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <>
                <AccountMultiSelect
                  accounts={accounts}
                  value={value}
                  onChange={onChange}
                  emptyLabel="No investment accounts available."
                />
                {error && <FieldHelper>{error.message}</FieldHelper>}
              </>
            )}
          />
          <FieldHelper>
            Current value is measured from these accounts.
          </FieldHelper>
        </Field>
      </FormCard>
    </FormPanel>
  );
};
